import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as crmApi from '../../../services/crmApi';
import * as fileApi from '../../../services/fileApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { CheckCircleIcon, CreditCardIcon } from '@heroicons/react/24/outline';

const formatDisplayName = (firstName, lastName, fallback = 'Unknown') =>
  [firstName, lastName].filter(Boolean).join(' ').trim() || fallback;

/**
 * PublicProcessingFeePayment Component
 * Public form for students to submit processing fee payment proof
 */
const PublicProcessingFeePayment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const [paymentData, setPaymentData] = useState({
    paymentMethod: '',
    referenceNumber: '',
    receiptDocument: null
  });

  useEffect(() => {
    fetchApplication();
  }, [token]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await crmApi.getApplicationByProcessingFeeToken(token);
      setApplication(response.data.data);
    } catch (error) {
      console.error('Error fetching application:', error);
      toast.error(error.response?.data?.message || 'Invalid or expired payment link');
      // Redirect after 3 seconds
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    try {
      setUploadingReceipt(true);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'Payment Receipt');
      formData.append('title', `Processing Fee Receipt - ${application.applicationNumber}`);
      formData.append('description', `Processing fee payment receipt for ${application.processingFee.currency} ${application.processingFee.amount}`);
      formData.append('entityType', 'application');
      formData.append('entityId', application.applicationNumber);

      const response = await fileApi.uploadFile(formData);
      const fileData = response.data.data;

      setPaymentData(prev => ({
        ...prev,
        receiptDocument: {
          fileId: fileData.id,
          fileName: fileData.originalName,
          fileSize: fileData.size
        }
      }));

      toast.success('Receipt uploaded successfully!');
    } catch (error) {
      console.error('Error uploading receipt:', error);
      toast.error(error.response?.data?.message || 'Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!paymentData.paymentMethod) {
      toast.error('Please select payment method');
      return;
    }

    if (!paymentData.referenceNumber?.trim()) {
      toast.error('Please enter payment reference number');
      return;
    }

    if (!paymentData.receiptDocument) {
      toast.error('Please upload payment receipt');
      return;
    }

    try {
      setSubmitting(true);

      await crmApi.submitProcessingFeePayment(token, paymentData);

      setSubmitted(true);
      toast.success('Payment submitted successfully!');
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error(error.response?.data?.message || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner message="Loading payment information..." />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for submitting your processing fee payment. Our team will verify your payment and update your application status shortly.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900">
              <strong>Application Number:</strong> {application.applicationNumber}
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid Payment Link</h2>
          <p className="text-gray-600">
            The payment link is invalid or has expired. Please contact support if you need assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white">
            <div className="flex items-center justify-center mb-4">
              <CreditCardIcon className="w-12 h-12" />
            </div>
            <h1 className="text-3xl font-bold text-center">Processing Fee Payment</h1>
            <p className="text-indigo-100 text-center mt-2">
              Submit your payment proof for verification
            </p>
          </div>

          <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-600 mb-1">Application Number</p>
                <p className="font-semibold text-gray-900">{application.applicationNumber}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Applicant Name</p>
                <p className="font-semibold text-gray-900">
                  {formatDisplayName(application.personalInfo.firstName, application.personalInfo.lastName, application.contactInfo?.email || 'Unknown')}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Program</p>
                <p className="font-semibold text-gray-900">{application.subProduct.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Processing Fee</p>
                <p className="text-xl font-bold text-indigo-600">
                  {application.processingFee.currency} {application.processingFee.amount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method <span className="text-red-500">*</span>
            </label>
            <select
              value={paymentData.paymentMethod}
              onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="">Select payment method</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Online Payment">Online Payment</option>
              <option value="Card">Card (Credit/Debit)</option>
              <option value="Cash">Cash</option>
              <option value="Cheque">Cheque</option>
              <option value="Mobile Payment">Mobile Payment</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Reference Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={paymentData.referenceNumber}
              onChange={(e) => setPaymentData({ ...paymentData, referenceNumber: e.target.value })}
              placeholder="Enter transaction/reference number"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the transaction ID or reference number from your payment
            </p>
          </div>

          <ProofAttachmentDropzone
            file={paymentData.receiptDocument}
            required
            hint={null}
            busy={uploadingReceipt}
            onChange={(file) => {
              if (!file) {
                setPaymentData((prev) => ({ ...prev, receiptDocument: null }));
                return;
              }
              handleReceiptUpload(file);
            }}
          />

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-900">
              <strong>Important:</strong> Please ensure the receipt clearly shows the transaction details, amount, and date. Our team will verify your payment within 1-2 business days.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting || uploadingReceipt || !paymentData.receiptDocument}
            className="w-full px-6 py-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                Submit Payment
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicProcessingFeePayment;
